import { type ReactNode } from 'react';
import { useDelayedPending } from '@/hooks/useDelayedPending';

interface AuthRouterBoundaryProps {
  children: ReactNode;
  isLoading: boolean;
  loadingFallback: ReactNode;
}

/**
 * Hold the router's first mount until the auth SDK has hydrated.
 *
 * That one wait is not removable: route guards read `context.auth`, and until
 * the SDK reports `isInitialized` there is no session verdict to read. It is
 * also short and synchronous once the SDK lands, so `useDelayedPending` usually
 * keeps the fallback from painting at all.
 *
 * It deliberately does NOT wait for canonical identity (ENG-1316) — the app
 * shell renders while `GET /auth/current` is in flight, and consumers that need
 * `user` show their own pending state. This boundary used to keep the mounted
 * router hidden for the rest of `isLoading` as well, to stop a half-finished
 * Stytch widget showing after it publishes a session. That cover now lives on
 * the two routes that host such a widget (`/authenticate`, `/reset-password`),
 * which is where it can apply without also blanking the app for everyone else.
 *
 * Consequence of dropping the hide: `isLoading` going true again would *unmount*
 * the router, taking the WebGPU context and its WASM heap with it. Nothing does
 * that today — Stytch never flips `isInitialized` back — but that is why
 * `isLoading` must stay
 * exactly "the SDK has not hydrated". It is not a general-purpose gate; anything
 * that needs to interrupt a running app belongs in an overlay, the way
 * `IdentityErrorBanner` handles a failed `/auth/current`.
 */
export function AuthRouterBoundary({
  children,
  isLoading,
  loadingFallback,
}: AuthRouterBoundaryProps) {
  const showBootFallback = useDelayedPending(isLoading);

  if (isLoading) return showBootFallback ? loadingFallback : null;
  return <>{children}</>;
}
